import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import {
  runBoxOfficeMismatchCheck,
  type TobisBoxOfficeMismatchCheck,
  type TobisBoxOfficeMismatchRow,
} from "@/lib/tobis";

// Manueller Diagnosecheck fuer model.match_tconst, siehe
// apps/bff/src/tobis/boxoffice-check.ts fuer die fachliche Begruendung.
// Eigene Datei statt ein weiterer Block in TobisAdminPage: die Seite hat
// schon zwei Boxen (Sources, Fetch log), und dieser Check hat sein eigenes
// kleines Ergebnis-Zustandsmodell (idle/loading/clean/anomalies/Fehler), das
// dort nur untergehen wuerde.

type Phase = "idle" | "loading" | "clean" | "anomalies" | "forbidden" | "notConfigured" | "failed";

function GrossAmount({ value }: { value: string }) {
  const intl = useIntl();
  return (
    <>
      {intl.formatNumber(Number(value), { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
    </>
  );
}

function AnomalyRow({ row }: { row: TobisBoxOfficeMismatchRow }) {
  return (
    <tr className="align-top text-ink/70">
      <td className="py-2 pr-4">{row.title ?? row.tconst}</td>
      <td className="py-2 pr-4">{row.year ?? "–"}</td>
      <td className="py-2 pr-4 text-right">
        <GrossAmount value={row.domesticGross} />
      </td>
      <td className="py-2 text-right">
        <GrossAmount value={row.worldwideGross} />
      </td>
    </tr>
  );
}

export function TobisBoxOfficeMismatchPanel() {
  const intl = useIntl();
  const [phase, setPhase] = useState<Phase>("idle");
  const [check, setCheck] = useState<TobisBoxOfficeMismatchCheck | null>(null);

  async function onRun() {
    setPhase("loading");
    const outcome = await runBoxOfficeMismatchCheck();

    if (outcome.status !== "ok") {
      setCheck(null);
      setPhase(outcome.status);
      return;
    }
    setCheck(outcome.check);
    setPhase(outcome.check.anomalies === 0 ? "clean" : "anomalies");
  }

  return (
    <div className="box bg-surface p-6">
      <div className="text-[10px] tracking-[0.25em] text-ink/55">
        {intl.formatMessage({
          id: "tobis.boxOfficeCheck.title",
          defaultMessage: "Box office sanity check",
        })}
      </div>

      <p className="mt-4 max-w-2xl text-sm text-ink/55">
        {intl.formatMessage({
          id: "tobis.boxOfficeCheck.intro",
          defaultMessage:
            "model.match_tconst matches box office rows that have no IMDb id by title and year, within a +/-1 year tolerance. That can in theory confuse two films with the same title released in different years (e.g. remakes). No case has been found yet, but domestic gross can never exceed worldwide gross, so a hit here is a strong sign of a wrong match.",
        })}
      </p>

      <div className="mt-4">
        <Button variant="primary" size="sm" onClick={() => void onRun()} disabled={phase === "loading"}>
          {phase === "loading"
            ? intl.formatMessage({ id: "tobis.boxOfficeCheck.running", defaultMessage: "Checking…" })
            : intl.formatMessage({ id: "tobis.boxOfficeCheck.run", defaultMessage: "Run check" })}
        </Button>
      </div>

      {phase === "forbidden" && (
        <p className="mt-4 text-sm text-ink/70">
          {intl.formatMessage({
            id: "tobis.boxOfficeCheck.forbidden",
            defaultMessage: "You lack the permission to run this check.",
          })}
        </p>
      )}
      {phase === "notConfigured" && (
        <p className="mt-4 text-sm text-ink/55">
          {intl.formatMessage({
            id: "tobis.notConfigured",
            defaultMessage:
              "No database configured. Set TOBIS_DATABASE_URL in the BFF environment and run the migration.",
          })}
        </p>
      )}
      {phase === "failed" && (
        <p className="mt-4 text-sm text-ink/55">
          {intl.formatMessage({
            id: "tobis.boxOfficeCheck.failed",
            defaultMessage: "The check could not be run.",
          })}
        </p>
      )}
      {phase === "clean" && check && (
        <p className="mt-4 text-sm text-ink/70">
          {intl.formatMessage(
            {
              id: "tobis.boxOfficeCheck.clean",
              defaultMessage: "No anomalies found across {total} checked rows.",
            },
            { total: intl.formatNumber(check.total) },
          )}
        </p>
      )}
      {phase === "anomalies" && check && (
        <>
          <p className="mt-4 text-sm text-ink/70">
            {intl.formatMessage(
              {
                id: "tobis.boxOfficeCheck.found",
                defaultMessage:
                  "{anomalies} of {total} checked rows have domestic gross higher than worldwide gross.",
              },
              { anomalies: intl.formatNumber(check.anomalies), total: intl.formatNumber(check.total) },
            )}
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[12px] text-ink/45">
                  <th className="py-2 pr-4 font-normal">
                    {intl.formatMessage({ id: "tobis.boxOfficeCheck.column.title", defaultMessage: "Title" })}
                  </th>
                  <th className="py-2 pr-4 font-normal">
                    {intl.formatMessage({ id: "tobis.boxOfficeCheck.column.year", defaultMessage: "Year" })}
                  </th>
                  <th className="py-2 pr-4 text-right font-normal">
                    {intl.formatMessage({
                      id: "tobis.boxOfficeCheck.column.domestic",
                      defaultMessage: "Domestic",
                    })}
                  </th>
                  <th className="py-2 text-right font-normal">
                    {intl.formatMessage({
                      id: "tobis.boxOfficeCheck.column.worldwide",
                      defaultMessage: "Worldwide",
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {check.rows.map((row) => (
                  <AnomalyRow key={row.tconst} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
